// Single HTTP client for the dashboard SPA. Components must not call fetch
// directly — see ../../docs/ui.md.

import type {
  AdminStats,
  AnalyticsOverview,
  AnalyticsSegments,
  ApiError,
  AutobanMode,
  AutobanPolicy,
  AutobanRule,
  Ban,
  BanDetail,
  BanSuggestion,
  Detection,
  DetectionEvidence,
  DetectionListParams,
  DetectionRuleSetting,
  DetectionRules,
  EdgeApp,
  EdgeApps,
  EnforcementAgents,
  Event,
  EventListParams,
  InsightsCountry,
  InsightsDomain,
  InsightsDomains,
  InsightsIP,
  InsightsPort,
  InsightsService,
  InstallBinaries,
  InstallParsersResponse,
  LiveTraffic,
  Page,
  Service,
  Source,
  Trace,
  AuditRow,
  TrustedIP,
  User,
} from "./types";

// In dev, Vite proxies /api → :8080; in prod the bundle hits VITE_API_PUBLIC_URL.
const API_BASE: string = (import.meta.env.VITE_API_PUBLIC_URL as string) || "";

export class HttpError extends Error {
  status: number;
  code: string;
  constructor(status: number, body: ApiError | string) {
    const msg = typeof body === "string" ? body : body.message;
    super(msg);
    this.status = status;
    this.code = typeof body === "string" ? "http" : body.error;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: { bearer?: string } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.bearer) headers["Authorization"] = `Bearer ${opts.bearer}`;
  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "include",
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (resp.status === 204) return undefined as T;
  const text = await resp.text();
  let parsed: unknown = text;
  if (text && resp.headers.get("Content-Type")?.includes("json")) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // keep as text
    }
  }
  if (!resp.ok) {
    if (resp.status === 401 && typeof window !== "undefined") {
      // Surface the redirect responsibility to the router.
      window.dispatchEvent(new CustomEvent("sm:unauthenticated"));
    }
    throw new HttpError(resp.status, parsed as ApiError | string);
  }
  return parsed as T;
}

// Auth.
export const auth = {
  login: (email: string, password: string) =>
    request<void>("POST", "/api/v1/auth/login", { email, password }),
  logout: () => request<void>("POST", "/api/v1/auth/logout"),
  me: () => request<User>("GET", "/api/v1/auth/me"),
  // Public: the sign-in screen has to know whether to offer the SSO button
  // before anybody is signed in. Never throws — an unreachable API leaves the
  // button off and the password form working.
  ssoStatus: async (): Promise<{ enabled: boolean }> => {
    try {
      return await request<{ enabled: boolean }>("GET", "/api/v1/auth/sso/status");
    } catch {
      return { enabled: false };
    }
  },
  // Sign-in and sign-out through the IdP are full-page navigations, not
  // fetches: both end in a redirect the browser has to follow, and the IdP has
  // to see its own cookies on its own origin.
  ssoLogin: (next?: string, prompt?: "none" | "login") => {
    const q = new URLSearchParams();
    if (next) q.set("next", next);
    // `prompt=none` asks the provider to answer without showing anything: it
    // either signs the visitor in or says "nobody is signed in", immediately.
    if (prompt) q.set("prompt", prompt);
    const query = q.toString();
    window.location.href = `${API_BASE}/api/v1/auth/sso/login${query ? `?${query}` : ""}`;
  },
  ssoLogout: () => {
    window.location.href = `${API_BASE}/api/v1/auth/sso/logout`;
  },
  // The operator's own observed IP (+ best-effort geo) — drives the "this is
  // your IP" badge across the UI.
  whoami: () =>
    request<{
      ip: string;
      country_code?: string;
      country_name?: string;
      city?: string;
    }>("GET", "/api/v1/whoami"),
};

// The dashboard's own audit log. The platform-wide view lives in Navitech ID.
export const audit = {
  list: (limit = 100, before?: number) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (before) q.set("before", String(before));
    return request<{ items: AuditRow[] }>("GET", `/api/v1/audit?${q}`);
  },
};

// Services.
export const services = {
  list: () => request<{ items: Service[] }>("GET", "/api/v1/services"),
  create: (s: Omit<Service, "id" | "created_at">) =>
    request<Service>("POST", "/api/v1/services", s),
  update: (
    id: string,
    patch: Partial<
      Pick<Service, "name" | "category" | "backend_address" | "exposure">
    >,
  ) => request<Service>("PATCH", `/api/v1/services/${id}`, patch),
  remove: (id: string) => request<void>("DELETE", `/api/v1/services/${id}`),
};

// Installer surface — used by the Sources page to build the agent install
// one-liner. See ../../../backend/internal/server/install_handler.go.
export const installer = {
  parsers: () =>
    request<InstallParsersResponse>("GET", "/api/install/parsers"),
  binaries: () => request<InstallBinaries>("GET", "/api/install/binaries"),
  // scriptURL returns the absolute URL the operator should curl on their host.
  // Token + (parser | preset) go in the query string — there is no session-cookie
  // auth on /install/agent.sh by design (the token IS the auth). Pass `preset`
  // for a multi-input bundle, or `parser` (+ optional log_path) for one input.
  scriptURL: (params: {
    token: string;
    parser?: string;
    preset?: string;
    name?: string;
    log_path?: string;
  }) => {
    const q = new URLSearchParams({ token: params.token });
    if (params.preset) q.set("preset", params.preset);
    else if (params.parser) q.set("parser", params.parser);
    if (params.name) q.set("name", params.name);
    if (params.log_path && !params.preset) q.set("log_path", params.log_path);
    const base = API_BASE || window.location.origin;
    return `${base}/api/install/agent.sh?${q.toString()}`;
  },
  // Ban-agent installer (F5 enforcement). Token is the only input; the script
  // installs the separate security-monitor-ban-agent in DRY-RUN. See bans.md.
  banAgentScriptURL: (token: string) => {
    const q = new URLSearchParams({ token });
    const base = API_BASE || window.location.origin;
    return `${base}/api/install/ban-agent.sh?${q.toString()}`;
  },
};

// Sources.
export const sources = {
  list: () => request<{ items: Source[] }>("GET", "/api/v1/sources"),
  create: (name: string, source_type: string, hostname?: string | null) =>
    request<{ source: Source; token: string }>("POST", "/api/v1/sources", {
      name,
      source_type,
      hostname: hostname ?? null,
    }),
  update: (
    id: string,
    patch: { name?: string; hostname?: string | null; enabled?: boolean },
  ) => request<Source>("PATCH", `/api/v1/sources/${id}`, patch),
  regenerateToken: (id: string) =>
    request<{ token: string }>(
      "POST",
      `/api/v1/sources/${id}/regenerate-token`,
    ),
  remove: (id: string) => request<void>("DELETE", `/api/v1/sources/${id}`),
};

// Trusted IPs.
export const trustedIps = {
  list: () => request<{ items: TrustedIP[] }>("GET", "/api/v1/trusted-ips"),
  create: (cidr: string, label: string, notes?: string | null) =>
    request<TrustedIP>("POST", "/api/v1/trusted-ips", {
      cidr,
      label,
      notes: notes ?? null,
    }),
  update: (id: string, patch: { label?: string; notes?: string | null }) =>
    request<void>("PATCH", `/api/v1/trusted-ips/${id}`, patch),
  remove: (id: string) => request<void>("DELETE", `/api/v1/trusted-ips/${id}`),
};

// Events. The full filter surface lives in `EventListParams`. Empty / undefined
// values are dropped so the URL stays short.
export const events = {
  list: (params: EventListParams = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params))
      if (v !== undefined && v !== "" && v !== null) q.set(k, String(v));
    const qs = q.toString() ? `?${q}` : "";
    return request<Page<Event>>("GET", `/api/v1/events${qs}`);
  },
  get: (id: string) => request<Event>("GET", `/api/v1/events/${id}`),
  /** Every span belonging to the same request as this event. */
  trace: (id: string) => request<Trace>("GET", `/api/v1/events/${id}/trace`),
};

// Admin / observability.
export const admin = {
  stats: () => request<AdminStats>("GET", "/api/v1/admin/stats"),
};

// Runtime feature flags — drives accurate ban-enforcement copy in the UI.
export const appInfo = {
  meta: () =>
    request<{ remediation_enabled: boolean; ban_workflow_enabled: boolean }>(
      "GET",
      "/api/v1/meta",
    ),
};

// Analytics — feeds the /traffic page.
export const analytics = {
  /** Recent arrivals with coordinates, for the live map. Cheap; poll it. */
  live: () => request<LiveTraffic>("GET", "/api/v1/analytics/live"),
  overview: (window: "1h" | "24h" | "7d" = "24h") =>
    request<AnalyticsOverview>(
      "GET",
      `/api/v1/analytics/overview?window=${window}`,
    ),
  segments: (window: "1h" | "24h" | "7d" = "24h") =>
    request<AnalyticsSegments>(
      "GET",
      `/api/v1/analytics/segments?window=${window}`,
    ),
};

// Per-entity drill-down. One round-trip per page — backend composes all
// sub-tables for the dimension. See backend-api.md.
// Edge guard — what the Hosted sidecar decided, per application.
// See ../../docs/edge-guard.md.
export const edge = {
  apps: (window: "1h" | "24h" | "7d" = "24h") =>
    request<EdgeApps>("GET", `/api/v1/edge/apps?window=${window}`),
  app: (app: string, env: string, window: "1h" | "24h" | "7d" = "24h") => {
    const q = new URLSearchParams({ window });
    if (env) q.set("env", env);
    return request<EdgeApp>(
      "GET",
      `/api/v1/edge/apps/${encodeURIComponent(app)}?${q.toString()}`,
    );
  },
};

export const insights = {
  ip: (ip: string, window: "1h" | "24h" | "7d" = "24h") =>
    request<InsightsIP>(
      "GET",
      `/api/v1/insights/ip/${encodeURIComponent(ip)}?window=${window}`,
    ),
  country: (code: string, window: "1h" | "24h" | "7d" = "24h") =>
    request<InsightsCountry>(
      "GET",
      `/api/v1/insights/country/${encodeURIComponent(code)}?window=${window}`,
    ),
  service: (id: string, window: "1h" | "24h" | "7d" = "24h") =>
    request<InsightsService>(
      "GET",
      `/api/v1/insights/service/${encodeURIComponent(id)}?window=${window}`,
    ),
  port: (port: number, protocol: string, window: "1h" | "24h" | "7d" = "24h") => {
    const q = new URLSearchParams({ window });
    if (protocol) q.set("protocol", protocol);
    return request<InsightsPort>(
      "GET",
      `/api/v1/insights/port/${port}?${q.toString()}`,
    );
  },
  domains: (window: "1h" | "24h" | "7d" = "24h") =>
    request<InsightsDomains>(
      "GET",
      `/api/v1/insights/domains?window=${window}`,
    ),
  domain: (host: string, window: "1h" | "24h" | "7d" = "24h") =>
    request<InsightsDomain>(
      "GET",
      `/api/v1/insights/domain/${encodeURIComponent(host)}?window=${window}`,
    ),
};

// Detections.
export const detections = {
  list: (params: DetectionListParams = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params))
      if (v !== undefined && v !== "" && v !== null) q.set(k, String(v));
    const qs = q.toString() ? `?${q}` : "";
    return request<Page<Detection>>("GET", `/api/v1/detections${qs}`);
  },
  get: (id: string) =>
    request<{ detection: Detection; evidence: DetectionEvidence[] }>(
      "GET",
      `/api/v1/detections/${id}`,
    ),
  close: (id: string) =>
    request<void>("POST", `/api/v1/detections/${id}/close`),
  reopen: (id: string) =>
    request<void>("POST", `/api/v1/detections/${id}/reopen`),
};

// Ban workflow (F5 start). Observation-only — approving records a decision and
// surfaces it; no firewall is touched. See ../../docs/bans.md.
export const banSuggestions = {
  list: (status = "pending") =>
    request<{ items: BanSuggestion[] }>(
      "GET",
      `/api/v1/ban-suggestions${status ? `?status=${status}` : ""}`,
    ),
  approve: (id: string, tierOverride?: number) =>
    request<Ban>("POST", `/api/v1/ban-suggestions/${id}/approve`, {
      tier_override: tierOverride ?? null,
    }),
  dismiss: (id: string) =>
    request<void>("POST", `/api/v1/ban-suggestions/${id}/dismiss`),
};

export const bans = {
  list: (params: { status?: string; client_ip?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.client_ip) q.set("client_ip", params.client_ip);
    const qs = q.toString() ? `?${q}` : "";
    // `total` is the real count; `items` is capped at `limit` for display.
    // Never present items.length as the total — that is how "always exactly
    // 500" reads as a fact about the data instead of about the query.
    return request<{ items: Ban[]; total: number; limit: number }>(
      "GET",
      `/api/v1/bans${qs}`,
    );
  },
  // The detail response carries the ban row plus the MEASURED effect — the
  // only field that reflects traffic rather than the decision.
  get: (id: string) => request<BanDetail>("GET", `/api/v1/bans/${id}`),
  // opts.detection_id links the ban back to the detection it was raised from.
  // opts.tier (1..5) chooses the ban period / permanent; omit to auto-escalate.
  create: (
    client_ip: string,
    opts: { note?: string; detection_id?: string; tier?: number } = {},
  ) =>
    request<Ban>("POST", "/api/v1/bans", {
      client_ip,
      note: opts.note ?? "",
      detection_id: opts.detection_id ?? "",
      tier: opts.tier ?? null,
    }),
  lift: (id: string) => request<void>("POST", `/api/v1/bans/${id}/lift`),
};

// Ban-agent fleet status. This is the only source of truth for whether a ban
// decision reached a kernel: the agents report what they applied each tick.
// See ../../docs/bans.md.
export const enforcement = {
  agents: () =>
    request<EnforcementAgents>("GET", "/api/v1/enforcement/agents"),
};

// Detection-rule tuning: thresholds, windows and composite weights. Severity is
// intentionally absent — the engine derives it from the score. See
// ../../docs/detections.md.
export const detectionRules = {
  get: () => request<DetectionRules>("GET", "/api/v1/detection-rules"),
  // Omit a field (or pass null) to reset it to the built-in default.
  set: (
    ruleKey: string,
    body: {
      enabled: boolean;
      threshold?: number | null;
      window_seconds?: number | null;
      weight?: number | null;
    },
  ) =>
    request<DetectionRuleSetting>(
      "PUT",
      `/api/v1/detection-rules/${ruleKey}`,
      body,
    ),
};

// Bulk export. Returns a URL rather than fetching: the response is a streamed
// download, so the browser should handle it, not the SPA's JSON client.
export const exports = {
  url: (
    resource: "events" | "detections" | "bans",
    format: "csv" | "json",
    params: Record<string, string> = {},
  ) => {
    const q = new URLSearchParams({ ...params, format });
    const base = API_BASE || window.location.origin;
    return `${base}/api/v1/export/${resource}?${q.toString()}`;
  },
};

// Auto-ban policy. Off by default; a created ban only reaches a firewall when
// REMEDIATION_ENABLED is also set. See ../../docs/autoban.md.
export const autoban = {
  get: () => request<AutobanPolicy>("GET", "/api/v1/autoban"),
  setMode: (mode: AutobanMode) =>
    request<{ mode: AutobanMode }>("PUT", "/api/v1/autoban/mode", { mode }),
  setRule: (
    ruleKey: string,
    body: {
      enabled: boolean;
      min_score: number;
      min_signals: number;
      tier_override: number | null;
    },
  ) => request<AutobanRule>("PUT", `/api/v1/autoban/rules/${ruleKey}`, body),
};
